import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  LogOut, Plus, Search, Eye, CheckCircle, Building2, ClipboardCheck, Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXIT_TYPES = [
  { value: "resignation", label: "Resignation" },
  { value: "termination", label: "Termination" },
  { value: "retirement", label: "Retirement" },
  { value: "end_of_contract", label: "End of Contract" },
];

const FEEDBACK_REASONS = [
  { value: "better_opportunity", label: "Better Opportunity" },
  { value: "personal", label: "Personal Reasons" },
  { value: "relocation", label: "Relocation" },
  { value: "higher_studies", label: "Higher Studies" },
  { value: "health", label: "Health Issues" },
  { value: "other", label: "Other" },
];

const CLEARANCE_DEPTS = [
  { key: "it_assets", label: "IT Assets" },
  { key: "access_cards", label: "Access Cards" },
  { key: "documents", label: "Documents" },
  { key: "finance", label: "Finance" },
  { key: "hr", label: "HR" },
  { key: "manager", label: "Manager" },
];

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-slate-100 text-slate-500",
};

const ExitManagement = () => {
  const { currentBusiness } = useAuth();
  const [exitRequests, setExitRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedExit, setSelectedExit] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    employee_id: "", resignation_date: "", last_working_day: "", exit_type: "resignation",
    reason: "", notice_period_days: 30, notice_period_served: false, shortfall_days: 0,
    feedback_reason: "",
  });

  const fetchExitRequests = useCallback(async () => {
    if (!currentBusiness?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/exit-requests?business_id=${currentBusiness.id}`);
      setExitRequests(res.data || []);
    } catch (err) {
      toast.error("Failed to load exit requests");
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?.id]);

  const fetchEmployees = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      const res = await api.get(`/employees?business_id=${currentBusiness.id}`);
      setEmployees((res.data || []).filter(e => e.is_active));
    } catch (err) {
      console.error("Failed to load employees");
    }
  }, [currentBusiness?.id]);

  useEffect(() => {
    fetchExitRequests();
    fetchEmployees();
  }, [fetchExitRequests, fetchEmployees]);

  const resetForm = () => {
    setFormData({
      employee_id: "", resignation_date: "", last_working_day: "", exit_type: "resignation",
      reason: "", notice_period_days: 30, notice_period_served: false, shortfall_days: 0,
      feedback_reason: "",
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.employee_id) {
      toast.error("Select an employee");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/exit-requests", { ...formData, business_id: currentBusiness.id });
      toast.success("Exit request created");
      setShowAddDialog(false);
      resetForm();
      fetchExitRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create exit request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateClearance = async (dept, status) => {
    if (!selectedExit) return;
    try {
      const res = await api.put(`/exit-requests/${selectedExit.id}/clearance/${dept}`, { status });
      setSelectedExit(res.data);
      toast.success(`${dept.replace("_", " ")} clearance updated`);
      fetchExitRequests();
    } catch (err) {
      toast.error("Failed to update clearance");
    }
  };

  const handleStartExit = async () => {
    if (!selectedExit) return;
    try {
      await api.put(`/exit-requests/${selectedExit.id}`, { status: "in_progress" });
      toast.success("Exit process started");
      fetchExitRequests();
      const res = await api.get(`/exit-requests?business_id=${currentBusiness.id}`);
      setSelectedExit(res.data.find(e => e.id === selectedExit.id) || selectedExit);
    } catch (err) {
      toast.error("Failed to start exit process");
    }
  };

  const handleCompleteExit = async () => {
    if (!selectedExit) return;
    if (!window.confirm("Complete exit and deactivate employee?")) return;
    try {
      await api.post(`/exit-requests/${selectedExit.id}/complete`);
      toast.success("Exit completed, employee deactivated");
      setShowViewDialog(false);
      fetchExitRequests();
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete exit");
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const url = `${BACKEND_URL}/api/exit-requests/export?business_id=${currentBusiness.id}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `exit_requests_${new Date().toISOString().split("T")[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Exit requests exported");
    } catch (err) {
      toast.error("Failed to export");
    }
  };

  const handleExitInterview = async (completed, notes) => {
    if (!selectedExit) return;
    try {
      await api.put(`/exit-requests/${selectedExit.id}`, {
        exit_interview_completed: completed,
        exit_interview_date: completed ? new Date().toISOString().split("T")[0] : null,
        exit_interview_notes: notes,
      });
      toast.success("Exit interview updated");
      fetchExitRequests();
    } catch (err) {
      toast.error("Failed to update exit interview");
    }
  };

  const filteredExits = exitRequests.filter(e => {
    const matchesSearch = !search || 
      e.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
      e.employee_code?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || e.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getClearanceProgress = (clearance) => {
    if (!clearance) return { cleared: 0, total: 0 };
    const total = Object.keys(clearance).length;
    const cleared = Object.values(clearance).filter(c => c.status === "cleared" || c.status === "na").length;
    return { cleared, total };
  };

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business to manage exits</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="exit-management-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Exit Management</h1>
          <p className="text-slate-500">Manage resignations, terminations, and exit clearances</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={handleExport}
            data-testid="export-exits-btn">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
          <Button className="btn-primary" onClick={() => { resetForm(); setShowAddDialog(true); }}
            data-testid="add-exit-btn">
            <Plus className="h-4 w-4 mr-2" /> New Exit Request
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-800">{exitRequests.length}</div>
            <div className="text-sm text-slate-500">Total Requests</div>
          </CardContent>
        </Card>
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{exitRequests.filter(e => e.status === "pending").length}</div>
            <div className="text-sm text-slate-500">Pending</div>
          </CardContent>
        </Card>
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{exitRequests.filter(e => e.status === "in_progress").length}</div>
            <div className="text-sm text-slate-500">In Progress</div>
          </CardContent>
        </Card>
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{exitRequests.filter(e => e.status === "completed").length}</div>
            <div className="text-sm text-slate-500">Completed</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-soft">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search by name or code..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-10 input-field" data-testid="search-exits" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="filter-exit-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Exit Requests Table */}
      <Card className="card-soft">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : filteredExits.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <LogOut className="h-12 w-12 mb-3 text-slate-300" />
              <p>No exit requests found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="font-semibold">Employee</TableHead>
                    <TableHead className="font-semibold">Exit Type</TableHead>
                    <TableHead className="font-semibold">Resignation Date</TableHead>
                    <TableHead className="font-semibold">Last Working Day</TableHead>
                    <TableHead className="font-semibold">Clearance</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExits.map(exit => {
                    const progress = getClearanceProgress(exit.clearance);
                    return (
                      <TableRow key={exit.id} className="hover:bg-slate-50/50" data-testid={`exit-row-${exit.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{exit.employee_name}</p>
                            <p className="text-xs text-slate-500">{exit.employee_code}</p>
                          </div>
                        </TableCell>
                        <TableCell className="capitalize">{exit.exit_type?.replace("_", " ")}</TableCell>
                        <TableCell>{exit.resignation_date || "-"}</TableCell>
                        <TableCell>{exit.last_working_day || "-"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div className="h-full bg-green-500 transition-all" 
                                style={{ width: `${progress.total ? (progress.cleared / progress.total * 100) : 0}%` }} />
                            </div>
                            <span className="text-xs text-slate-500">{progress.cleared}/{progress.total}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${STATUS_COLORS[exit.status] || "bg-slate-100"} capitalize`}>
                            {exit.status?.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end">
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedExit(exit); setShowViewDialog(true); }}
                              data-testid={`view-exit-${exit.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Exit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Exit Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select value={formData.employee_id} onValueChange={v => setFormData({...formData, employee_id: v})}>
                <SelectTrigger data-testid="exit-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Exit Type</Label>
                <Select value={formData.exit_type} onValueChange={v => setFormData({...formData, exit_type: v})}>
                  <SelectTrigger data-testid="exit-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXIT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notice Period (days)</Label>
                <Input type="number" value={formData.notice_period_days} 
                  onChange={e => setFormData({...formData, notice_period_days: parseInt(e.target.value) || 0})}
                  data-testid="exit-notice-days-input" />
              </div>
              <div className="space-y-2">
                <Label>Resignation Date</Label>
                <Input type="date" value={formData.resignation_date} 
                  onChange={e => setFormData({...formData, resignation_date: e.target.value})}
                  data-testid="exit-resignation-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Last Working Day</Label>
                <Input type="date" value={formData.last_working_day} 
                  onChange={e => setFormData({...formData, last_working_day: e.target.value})}
                  data-testid="exit-lwd-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={formData.feedback_reason} onValueChange={v => setFormData({...formData, feedback_reason: v})}>
                <SelectTrigger data-testid="exit-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  {FEEDBACK_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Additional Details</Label>
              <Textarea value={formData.reason} onChange={e => setFormData({...formData, reason: e.target.value})}
                placeholder="Additional details about the exit..." rows={2} data-testid="exit-details-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" disabled={submitting} data-testid="create-exit-btn">
                {submitting ? "Creating..." : "Create Request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View/Manage Exit Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Exit Details</DialogTitle>
          </DialogHeader>
          {selectedExit && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Employee</p>
                    <p className="font-medium">{selectedExit.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Code</p>
                    <p className="font-medium">{selectedExit.employee_code}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Exit Type</p>
                    <p className="font-medium capitalize">{selectedExit.exit_type?.replace("_", " ")}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Status</p>
                    <Badge className={`${STATUS_COLORS[selectedExit.status]} capitalize`}>
                      {selectedExit.status?.replace("_", " ")}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-slate-500">Resignation Date</p>
                    <p className="font-medium">{selectedExit.resignation_date || "-"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Last Working Day</p>
                    <p className="font-medium">{selectedExit.last_working_day || "-"}</p>
                  </div>
                </div>
              </div>

              {/* Clearance Checklist */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5" /> Clearance Checklist
                </h3>
                <div className="space-y-2">
                  {CLEARANCE_DEPTS.map(dept => {
                    const clearance = selectedExit.clearance?.[dept.key] || {};
                    return (
                      <div key={dept.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Checkbox checked={clearance.status === "cleared" || clearance.status === "na"}
                            onCheckedChange={(checked) => handleUpdateClearance(dept.key, checked ? "cleared" : "pending")}
                            disabled={selectedExit.status === "completed"}
                            data-testid={`clearance-${dept.key}`} />
                          <span className="font-medium">{dept.label}</span>
                        </div>
                        <Badge className={clearance.status === "cleared" ? "bg-green-100 text-green-700" : 
                          clearance.status === "na" ? "bg-slate-100 text-slate-500" : "bg-yellow-100 text-yellow-700"}>
                          {clearance.status === "na" ? "N/A" : clearance.status?.charAt(0).toUpperCase() + clearance.status?.slice(1) || "Pending"}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Exit Interview */}
              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold text-slate-800 mb-3">Exit Interview</h3>
                <div className="flex items-center gap-3">
                  <Checkbox checked={selectedExit.exit_interview_completed}
                    onCheckedChange={(checked) => handleExitInterview(checked, selectedExit.exit_interview_notes || "")}
                    disabled={selectedExit.status === "completed"}
                    data-testid="exit-interview-checkbox" />
                  <span>Exit Interview Completed</span>
                </div>
                {selectedExit.exit_interview_completed && selectedExit.exit_interview_date && (
                  <p className="text-sm text-slate-500 mt-2">Conducted on: {selectedExit.exit_interview_date}</p>
                )}
              </div>

              {/* Actions */}
              {selectedExit.status !== "completed" && selectedExit.status !== "cancelled" && (
                <div className="flex gap-3">
                  {selectedExit.status === "pending" && (
                    <Button onClick={handleStartExit} className="btn-primary" data-testid="start-exit-btn">
                      Start Exit Process
                    </Button>
                  )}
                  {selectedExit.status === "in_progress" && (
                    <Button onClick={handleCompleteExit} variant="outline" className="text-green-600 border-green-200"
                      data-testid="complete-exit-btn">
                      <CheckCircle className="h-4 w-4 mr-2" /> Complete Exit
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExitManagement;
